import { GetServerSideProps, NextPage } from 'next';
import React from 'react';
import styled from 'styled-components';

import { pagesConfigStore } from '~/atomic/atom/links/pagesConfigStore';
import { CommonLayout } from '~/atomic/layout/CommonLayout';
import { LayoutType } from '~/atomic/layout/DefaultLayout';
import { PageTemplate } from '~/atomic/organism/PageTemplate/PageTemplate';
import { GenrePage } from '~/atomic/page/catalog/genrePage/GenrePage';
import {
  getBooks,
  getGenrePageDescription,
} from '~/atomic/page/catalog/genrePage/genrePage.data';
import {
  genrePageSeoSelector,
  isHidePageSelector,
} from '~/atomic/page/catalog/genrePage/genrePage.seo.selector';
import { getGenresFromCookieAndSave } from '~/atomic/page/index/feed.data';
import { checkAuthToken } from '~/feature/authorization/checkAuthToken';
import { getAuthTokenFromServer } from '~/feature/authorization/getAuthToken';
import {
  checkVMDParamsInQueries,
  getFilterFromUrl,
} from '~/feature/catalog/filter/catalogFilter.data';
import {
  catalogFilterGenresSelector,
  catalogFilterSelector,
  catalogPageTagSelector,
} from '~/feature/catalog/filter/catalogFilter.selector';
import { catalogFilterActions } from '~/feature/catalog/filter/catalogFilter.slice';
import { getPagingFromUrl } from '~/feature/pagination/pagination.data';
import { redisClient } from '~/feature/server/redisClient';
import { checkSupportWebp } from '~/feature/supportWebp/checkSupportWebp';
import {
  createStore, RootState, useAppSelector,
} from '~/store';

const CatalogGenrePage:NextPage & LayoutType = () => {
  const { meta, og } = useAppSelector(genrePageSeoSelector);
  const isHidePage = useAppSelector(isHidePageSelector);

  return (
    <PageTemplate
      isPublic={pagesConfigStore.genre.isPublic}
      seo={{
        title: og.title,
        description: og.description,
        canonical: meta.canonical,
        customSeoTags: isHidePage && (
          <meta name="robots" content="noindex, nofollow" />
        ),
      }}
      content={(
        <>
          <SCCatalogPage>
            <GenrePage />
          </SCCatalogPage>
        </>
      )}
    />
  );
};

CatalogGenrePage.getLayout = (page) => (
  <CommonLayout>{ page }</CommonLayout>
);

const SCCatalogPage = styled.div`
  display: flex;
  flex-direction: column;
  padding-top: 12px;
  margin-bottom: 52px;
`;

export const getServerSideProps: GetServerSideProps<{ state: RootState; }> = async (context) => {
  const { dispatch, store } = await createStore();
  const isLoggedIn = await checkAuthToken(context, dispatch);
  const token = getAuthTokenFromServer(context);

  dispatch(catalogFilterActions.changePageUrlStartWith(pagesConfigStore.genre.url()));
  await dispatch(getFilterFromUrl(context));
  await dispatch(checkVMDParamsInQueries(context));

  const { url } = context.req;
  let cache = null;
  try {
    cache = JSON.parse(await redisClient.get(url));
  } catch (e) {
    console.error(`Redis cache error for genre page url=${url}:`, e);
  }
  const genres = catalogFilterGenresSelector(store.getState());
  let state: RootState;

  if (cache && !isLoggedIn && genres.length !== 0) {
    state = cache;
    console.log(`from cache genre page with url=${url}`);
  } else {
    await dispatch(getPagingFromUrl(context));
    await dispatch(getGenresFromCookieAndSave(context));
    await Promise.all([
      dispatch(getBooks({ token })),
      dispatch(getGenrePageDescription()),
    ]);

    state = store.getState();
    const genreTag = catalogPageTagSelector(state);
    const { tagSlug } = catalogFilterSelector(state);

    if (tagSlug !== '' && !genreTag?.name) {
      return { notFound: true };
    }

    if (!isLoggedIn) {
      redisClient.set(url, JSON.stringify(state), 'EX', 60 * 15);
    }
  }

  checkSupportWebp(context.req, dispatch);
  return { props: { state } };
};

export default CatalogGenrePage;
